import React, {useMemo} from 'react';
import {FlatList, Pressable, StyleSheet, View} from 'react-native';
import {useHeaderHeight} from '@react-navigation/elements';
import {observer} from 'mobx-react-lite';

import {useTabBarBottomPadding} from '../hooks/useTabBarBottomPadding';
import type {Segment} from '../stores/feedStore';
import {useStores} from '../stores/use-stores';

import Text from './ui-controls/Text';
import {Icon} from './Icon';

const DiscoverFilters = observer(() => {
  const {feedStore, globalStore} = useStores();
  // DIS-1209: Discover route uses headerTransparent:true, so this side-drawer
  // FlatList must inset for the header (top) and animated tab bar (bottom).
  // Without these, top filter rows hide behind the header (raised too high).
  const headerHeight = useHeaderHeight();
  const tabBarPadding = useTabBarBottomPadding({skipFocusReset: true});
  const contentContainerStyle = useMemo(
    () => ({paddingTop: headerHeight, paddingBottom: tabBarPadding}),
    [headerHeight, tabBarPadding],
  );
  const scrollIndicatorInsets = useMemo(
    () => ({top: headerHeight, bottom: tabBarPadding}),
    [headerHeight, tabBarPadding],
  );

  const separator = () => {
    return <View style={styles.separator} />;
  };

  const selectSegment = (segment: Segment) => {
    globalStore.setFiltersVisible(false);
    feedStore.selectSegment(segment);
  };

  const renderSegment = ({item}: {item: Segment}) => {
    return (
      <Pressable onPress={() => selectSegment(item)}>
        <View style={styles.segmentRow} testID="discover-filter">
          <Text numberOfLines={1} ellipsizeMode="tail" style={styles.segmentText}>
            {item.label}
          </Text>
          <Icon name="chevron-right" size={12} color="lightgrey" />
        </View>
      </Pressable>
    );
  };

  return (
    <FlatList
      data={feedStore.segments}
      renderItem={renderSegment}
      ItemSeparatorComponent={separator}
      style={styles.scrollView}
      contentContainerStyle={contentContainerStyle}
      scrollIndicatorInsets={scrollIndicatorInsets}
      keyExtractor={item => item.value}
    />
  );
});

export default DiscoverFilters;

const styles = StyleSheet.create({
  // Segment list
  scrollView: {
    width: '100%',
  },
  segmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 5,
    left: 1,
  },
  segmentText: {
    fontSize: 16,
    marginLeft: 8,
    textTransform: 'capitalize',
  },
  separator: {
    marginVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: 'lightgrey',
  },
});
